import _ from 'underscore';
import axios from 'axios';
import appRouter from '@/router';
import { SERVICE_URL } from "../../_commons";
import getReCaptchaHeaderTBR from "@/utils/recaptchaTBR";

const namespaced = true;

const state = () => ({
  customerName: '',
  dateOfBirth: '',
  lastFourDigitsOfSsn: '',
  licenseNumber: '',
  ddNumber: '',
  plateType: {},

  customerId: '',
  duration: 0,
  sessionStartDate: '',
  sessionEndDate: '',
  sessionUid: '',
  accessToken: '',
  vehicleRegistrationId: '',
  personalInformation: null,
  credentialInformation: null,
  blocksInformation: null,
  registrationInformation: null,
  adjudicationLicenseInformation: null,
  adjudicationCommercialLicenseInformation: null,
  adjudicationRegistrationInformation: null,
  renewRegistration: null,
  selectRenewRegistartion: null,
  reservationInformation: null,
  paperlessEnrollmentInformation: null,

  businessPersonalInformation: null,
  businessRegistrationInformation: null,
  businessAdjudicationRegistrationInformation: null,
  businessRegistrationSearchResults: null,
  businessBlocksInformation: null,
  businessReservationInformation: null,

  cartTransactions: '',
  extensionData: '',
  onlineTransactionFeeCollection: [],
  allFeesCollection: [],
  transactionCartId: '',

  renewalCart: [],

  totalBill: null,
  cartTotal : null,

  step: 1,

  duplicateReg: false,
  cancelReg: false,


  outputDocumentInformation: null,
  modalDocumentType: false,
  VehicleRegIdTxnId: [],

  activeAlert: false,
  crashLocation: {},
  mfaStep: 1,
  mfaType: null,
  btnDisable: false,

  contactUpdateStep: 1,

  navigationSource: null, // Add this to track the "pending" destination

  updateContact: null,
  contactUpdateInfo: null,
  contactVerifyEmail: false,
  contactVerifyPhone: false,
  createContact: false,
  contactMfaRequest: [],

  addressUpdateStep: 1,

  userPhysicalAddress: [],
  userMailingAddress: [],
  standardPhysicalAddress: {},
  standardMailingAddress: {},
  skipStepTo: false,

  loginMode: null, // "individual" or "business"
  loginTypeId: null, // 1, 2, 3, or 4
  isBusinessUser: false,
});

const mutations = {
  setState(state, data) {
    // In Vue 3, direct assignment is reactive
    state[data.property] = data.value;
  }
};
  
const actions = {
  /**
   * @param state
   * @param commit
   * @param rootState
   * @returns {Promise<void>}
   */
  async getGuestLoginRequest({ rootState, state, commit }) {
    const endpoint = `${SERVICE_URL}customeraccountlogin/guestloginrequest`;

    // loginTypeId to number
    const loginType = Number(state.loginTypeId);

    // BUSINESS vs INDIVIDUAL LOGIC BASED ON DOB -
    // DOB is required for individual login types but not for business, 
    // so we will send a min date for business logins to satisfy backend requirement of DOB field while signaling that it's a business login.
    const minDate = "1753-01-01";

    const isBusiness = state.loginMode === "business";

    // DOB value sent to backend is either the user's input DOB (for individual) or min date (for business)
    const finalDob = isBusiness ? minDate : state.dateOfBirth;

    // Lastname (orgName for business, last name for individual)
    const lastname = state.customerName;

    // PAYLOAD
    const dataObj = {
      loginTypeId: loginType,
      loginLicense: null,
      loginVehicle: null,
      loginAdjudication: null,
      loginCrash: null
    };

    // LOGIN TYPE 
    switch (loginType) {
      case 1: // LICENSE LOGIN (individual only)
        dataObj.loginLicense = {
          dateOfBirth: finalDob,
          lastFourSssn: state.lastFourDigitsOfSsn,
          lastname,
          licenseNumber: state.licenseNumber
        };
        break;

      case 2: {
        const isBusiness = state.loginMode === "business";
        const isPlate = state.plateIdType === "plate";
        //  plateNumber or titleNumber based on plateIdType from LoginForm
        const plateNumberValue = isPlate ? state.plateNumber : state.titleNumber;
        // plateTypeId
        const plateTypeIdValue = isPlate ? state.plateType?.code : 0; // title login uses 0

        dataObj.loginVehicle = {
          dateOfBirth: finalDob,
          lastname,
          vinLastSix: state.vinLastSix,
          // Always send plateNumber, even for title login
          plateNumber: plateNumberValue,
          // Individual users ALWAYS send real plateTypeId
          // Business users send 0 only for title login
          plateTypeId: isBusiness ? plateTypeIdValue : state.plateType?.code
        };

        break;
      }
      case 3: // ADJUDICATION LOGIN
        dataObj.loginAdjudication = {
          caseSummonTicketNumber: state.caseSummonTicketNumber,
          dateOfBirth: finalDob,
          lastname
        };

        if (state.suspensionEffectiveDate) {
          dataObj.loginAdjudication.suspensionEffectiveDate = state.suspensionEffectiveDate;
        }
        if (state.violationDate) {
          dataObj.loginAdjudication.violationDate = state.violationDate;
        }
        break;

      case 4: // CRASH LOGIN
        dataObj.loginCrash = {
          crashDate: state.crashDate || '',
          crashLocationId: state.crashLocation?.code,
          dateOfBirth: finalDob,
          lastname
        };
        break;
    }

    commit("setState", { property: "isBusinessUser", value: isBusiness });

    const config = await getReCaptchaHeaderTBR('post', endpoint);

    axios.post(endpoint, dataObj, config)
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) {
        const data = result.data;
        commit('setState', { property: 'loginUserDetails', value: data });
        appRouter.push({ name: 'MultiFactAuth' });

        if (data.isPaperOtpSent) {
          commit('setState', { property: 'mfaStep', value: 2 });
          commit('setState', { property: 'mfaType', value: 3 });
        } else if (!data.email && !data.phone) {
          commit('setState', { property: 'mfaStep', value: 3 });
        } else {
          commit('setState', { property: 'mfaStep', value: 1 });
        }
      }
    })
    .catch(error => {
      console.error(error);
    });
  },

  async getGuestLoginMfaRequest({ rootState, state, commit }, data) {
    const endpoint = `${SERVICE_URL}customeraccountlogin/guestloginmfarequest`;
    const payload = {
      loginRequestToken: state.loginUserDetails.loginRequestToken,
      mfaType: data.mfaType,
      mfaTypeValue: data.mfaTypeValue
    }

    const config = await getReCaptchaHeaderTBR('post', endpoint);
    axios.post(endpoint, payload, config)
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) {
        if(data.mfaType == 4) {
          commit('setState', { property: 'mfaStep', value: 4 });
        }
        else if(data.mfaType == 3) {
          commit('setState', { property: 'mfaStep', value: 5 });
        }
        else {
          commit('setState', { property: 'loginMfaRequest', value: result.data });
          commit('setState', { property: 'mfaStep', value: 2 });
          commit('setState', { property: 'mfaType', value: data.mfaType });
        }
      }
    })
    .catch(error => {
      console.error(error);
    });
  },

  async getGuestLogin({ rootState, state, commit }, data) {
    const endpoint = `${SERVICE_URL}customeraccountlogin/guestlogin`;
    const payload = {
      loginRequestToken: state.loginUserDetails.loginRequestToken,
      mfaType: state.mfaType,
      referrer: state.sourceReferrer || '', // Use the sourceReferrer from state, default to empty string if not set
      verificationCode: data.verificationCode
    }
    
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    axios.post(endpoint, payload, config)
    .then(result => {
      // move on to profile screen IFF it's a 200
      if (result.status === rootState.STATUSES.SUCCESS) {
        commit('setState', { property: 'accessToken', value: result.data.accessToken });
        commit('setState', { property: 'duration', value: result.data.duration });
        if(state.loginUserDetails.isPaperOtpSent && state.mfaType == 3) {
          commit('setState', { property: 'contactUpdateStep', value: 1 });
          commit('setState', { property: 'createContact', value: true });
          appRouter.push({ name: 'UpdateContacts' });
        }
        else {
          appRouter.push({ name: 'Profile' });
        }
      }
    })
    .catch(error => {
      console.error(error);
      if(data.attemptValue == 3) {
        setTimeout(() => {
          appRouter.push({ name: 'GuestSignin' });
        }, 10000)
      }
    });
  },


  async getBusinessGuestLogin({ rootState, state, commit }, data) {
    const endpoint = `${SERVICE_URL}customeraccountlogin/guestlogin`;
    const payload = {
      loginRequestToken: state.loginUserDetails.loginRequestToken,
      mfaType: state.mfaType,
      referrer: state.sourceReferrer || '', // Use the sourceReferrer from state, default to empty string if not set
      verificationCode: data.verificationCode
    }

    const config = await getReCaptchaHeaderTBR('post', endpoint);
    try {
      const result = await axios.post(endpoint, payload, config);

      if (result.status === rootState.STATUSES.SUCCESS) {
        commit("setState", { property: "accessToken", value: result.data.accessToken });
        commit("setState", { property: "duration", value: result.data.duration });

        if(state.loginUserDetails.isPaperOtpSent && state.mfaType == 3) {
          commit('setState', { property: 'contactUpdateStep', value: 1 });
          commit('setState', { property: 'createContact', value: true });
          appRouter.push({ name: 'BusinessUpdateContacts' });
        }
        else {
          appRouter.push({ name: 'BusinessProfile' });
        }
        // Business users do NOT go through contact update flow
        // appRouter.push({ name: "BusinessProfile" });
      }
    } catch (error) {
      console.error(error);

      if (data.attemptValue === 3) {
        setTimeout(() => {
          appRouter.push({ name: "GuestSignin" });
        }, 10000);
      }
    }
  },

  async completeGuestLogin({ state, dispatch }, data) {
    if (state.isBusinessUser) {
      return dispatch("getBusinessGuestLogin", data);
    }
    return dispatch("getGuestLogin", data);
  },

  /**
   * @param context
   * @param data 
   * @returns {Promise<void>}
   */
  async setGuestLoginData(context, data) {
    const { runLookup, ...props } = data;
    // save module properties
    _.mapObject(props, (val, key) => {
      context.commit('setState', { property: key, value: val });
    });

    // do lookup?
    if (runLookup) {
      await context.dispatch('getGuestLoginRequest');
    }
  },

  async getGuestSendMfaRequest({ rootState, state, commit }, data) {
    const endpoint = `${SERVICE_URL}customeraccount/sendmfarequest`;
    const payload = {
      email: data.email,
      phone: data.phone
    }
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);

    axios.post(`${endpoint}?accessToken=${token}`, payload, config)
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) {
        // existing updateContact structure and update email and phone and not whole object. 
        let contactData = {
          ...(state.updateContact || {}),  // Keep existing fields, default to empty object if null
          email: Boolean(data.email),                    
          phone: Boolean(data.phone)                     
        }
        // Only proceed if this is not a resend OTP request
        if(!data.resendOtp) {
          commit('setState', { property: 'updateContact', value: contactData });
          commit('setState', { property: 'contactUpdateInfo', value: data });
          commit('setState', { property: 'contactUpdateStep', value: 3 });
          // Reset verification flags to ensure a fresh MFA process for each request
          commit('setState', { property: 'contactMfaRequest', value: result.data });
          commit('setState', { property: 'contactVerifyEmail', value: false });
          commit('setState', { property: 'contactVerifyPhone', value: false });
        }
      }
    })
    .catch(error => {
      console.error(error);
    });
  },

  async getGuestVerifyMfa({ rootState, state, commit }, data) {
    const endpoint = `${SERVICE_URL}customeraccount/verifymfacode`;
    const payload = {
      mfaType: data.mfaType,
      verificationCode: data.verificationCode
    }
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);
    
    axios.post(`${endpoint}?accessToken=${token}`, payload, config)
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) { 
        if(data.mfaType == 2) {
          commit('setState', { property: 'contactVerifyEmail', value: true });
        }
        if(data.mfaType == 1) {
          commit('setState', { property: 'contactVerifyPhone', value: true });
        }
      }
    })
    .catch(error => {
      console.error(error);
      if(data.attemptValue == 3) {
        commit('setState', { property: 'btnDisable', value: true });
        setTimeout(() => {
          appRouter.push({ name: 'GuestSignin' });
        }, 10000)
      }
    });
  },

  /**
 * @param state
 * @param commit
 * @param rootState
 * @returns {Promise<void>}
 */

  async getCreateTransactionService({state, commit, rootState }, isEligible) { 
    let transactionTypeId;
    if (state.duplicateReg) {
      transactionTypeId = 198;
    } else if (state.cancelReg) {
      transactionTypeId = 199;
    } else {
      transactionTypeId = 197;
    }

    const endpoint = `${SERVICE_URL}transactionservice`
    const data = {
      'transactionReasonId': 0, 
      'transactionTypeId': transactionTypeId
    };
    const config = await getReCaptchaHeaderTBR('post', endpoint);

    const token = encodeURIComponent(state.accessToken);

    axios.post(`${endpoint}?accessToken=${token}&OnlineTransactionEligibility=${isEligible}`, data, config)
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) {
        commit('setState', { property: 'cartTransactions', value: result.data });
        commit('setState', { property: 'step', value: 2 });
        commit('setState', { property: 'onlineTransactionFeeCollection', value: null });

        state.VehicleRegIdTxnId.push({ vehicleRegistrationId: state.selectRenewRegistartion.vehicleRegistrationId, transactionId: result.data.cartTransactions[0].transactionData.transactionId })
      }
    })
    .catch(error => {
      console.error(error);
    });

  },

  async getSaveWipData({state, rootState, dispatch, commit}) {
    let endpoint = '';

    if (state.duplicateReg) {
      endpoint = `${SERVICE_URL}duplicateregistration/saveworkinprogressdata`;
    } else if (state.cancelReg) {
      endpoint = `${SERVICE_URL}cancelregistration/saveworkinprogressdata`;
    } else {
      endpoint = `${SERVICE_URL}renewregistration/saveworkinprogressdata`;
    }
  
    const config = await getReCaptchaHeaderTBR('post', endpoint);
      
    let cartTxn = state.cartTransactions.cartTransactions[0];

    const headers = {
      'accept': 'application/json',
      'Content-Type': 'application/json'
    }
    const token = encodeURIComponent(state.accessToken);

    axios.post(`${endpoint}?accessToken=${token}&vehicleRegistrationId=${state.selectRenewRegistartion.vehicleRegistrationId}&transactionId=${cartTxn.transactionData.transactionId}`, headers, config )
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) {
        if(state.cancelReg){
          commit('setState', {property: 'step', value: 3});
        } 
        else {
          dispatch('getRegistrationRenewalFee')
        } 
      }
    })
    .catch(error => {
      console.error(error);
    });

  },

  async getAddTransaction({state, rootState, commit }, isEligible) {
    const endpoint = `${SERVICE_URL}transactionservice/addtransaction`
    const config = await getReCaptchaHeaderTBR('post', endpoint);

    let transactionTypeId;
    if (state.duplicateReg) {
      transactionTypeId = 198;
    } else if (state.cancelReg) {
      transactionTypeId = 199;
    } else {
      transactionTypeId = 197;
    }

    let arrayCartTxns = [];
    for(let i=0; state.cartTransactions.cartTransactions.length > i; i++) {
      arrayCartTxns.push({
        transactionData: state.cartTransactions.cartTransactions[i].transactionData,
        transactionFees: state.renewalCart[i]?.onlineTransactionFeeCollection
      })
    }
    
    const data = { 
      transactionCart: {
        cartTransactions: arrayCartTxns,
        email: state.cartTransactions.email,
        isPaperlessCommunication: state.cartTransactions.isPaperlessCommunication,
        phone: state.cartTransactions.phone,
        transactionCartId: state.cartTransactions.transactionCartId, 
      },
      transactionRequest: {
        transactionReasonId: 0,
        transactionTypeId: transactionTypeId
      }
    };
    
    const token = encodeURIComponent(state.accessToken);

    axios.post(`${endpoint}?accessToken=${token}&OnlineTransactionEligibility=${isEligible}`, data, config)
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) {
        commit('setState', { property: 'cartTransactions', value: result.data });
        commit('setState', {property: 'step', value: 2});
        commit('setState', { property: 'onlineTransactionFeeCollection', value: null });
       
        state.VehicleRegIdTxnId.push({vehicleRegistrationId: state.selectRenewRegistartion.vehicleRegistrationId,transactionId: result.data.cartTransactions[0].transactionData.transactionId})
      }
    })
    .catch(error => {
      console.error(error);
    });
  },

  async getRemoveTransaction({state, rootState, commit}, data) {
    const endpoint = `${SERVICE_URL}transactionservice/removetransaction`;
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);
    commit('setState', { property: 'isLoading', value: true });

    let currentTxn = [];
    if (data != null) {
      currentTxn = state.cartTransactions.cartTransactions[data];
    } else {
      currentTxn = state.cartTransactions.cartTransactions[0];
    }

    let arrayCartTxns = [];
    for(let i=0; state.cartTransactions.cartTransactions.length > i; i++) {
      arrayCartTxns.push({
        transactionData: state.cartTransactions.cartTransactions[i].transactionData,
        transactionFees: state.cartTransactions.cartTransactions[i].transactionFees != null ? state.cartTransactions.cartTransactions[i].transactionFees : state.renewalCart[i]?.onlineTransactionFeeCollection
      })
    }
    const payload = { 
      transactionCart: {
        cartTransactions: arrayCartTxns,
        email: state.cartTransactions.email,
        isPaperlessCommunication: state.cartTransactions.isPaperlessCommunication,
        phone: state.cartTransactions.phone,
        transactionCartId: state.cartTransactions.transactionCartId, 
      },
      transactionData: currentTxn.transactionData
    };
    let result = undefined;
    try {
      result = await axios.post(`${endpoint}?accessToken=${token}`, payload, config);
      if (result.status === rootState.STATUSES.SUCCESS) {

        if (data != null) {
          state.renewalCart.splice(data, 1);
          state.cartTransactions.cartTransactions.splice(data, 1);
          if (state.renewalCart.length === 0) {
              if (state.isBusinessUser) {
              appRouter.push({ name: 'BusinessProfile' });
            } else{
              appRouter.push({ name: 'Profile' });
            }
          } 
          if(state.renewalCart.length === 0) {
            commit('setState', { property: 'step', value: 1 });
          } 
        } else {
          state.cartTransactions.cartTransactions.shift();
        }
      }
    } catch (error) {
      result = error.response;
    }
    return result;
  },

  async getCancelTransaction({state, rootState, commit}, data) {
    const endpoint = `${SERVICE_URL}transactionservice/removetransaction`;
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);

    let currentTxn = [];
    if (data != null) {
      currentTxn = state.cartTransactions.cartTransactions[data];
    } else {
      currentTxn = state.cartTransactions.cartTransactions[0];
    }

    let arrayCartTxns = [];
    for(let i=0; state.cartTransactions.cartTransactions.length > i; i++) {
      arrayCartTxns.push({
        transactionData: state.cartTransactions.cartTransactions[i].transactionData,
        transactionFees: state.cartTransactions.cartTransactions[i].transactionFees != null ? state.cartTransactions.cartTransactions[i].transactionFees : state.onlineTransactionFeeCollection
      })
    }
    const payload = { 
      transactionCart: {
        cartTransactions: arrayCartTxns,
        email: state.cartTransactions.email,
        isPaperlessCommunication: state.cartTransactions.isPaperlessCommunication,
        phone: state.cartTransactions.phone,
        transactionCartId: state.cartTransactions.transactionCartId, 
      },
      transactionData: currentTxn.transactionData
    };
    let result = undefined;
    try {
      result = await axios.post(`${endpoint}?accessToken=${token}`, payload, config);
      if (result.status === rootState.STATUSES.SUCCESS) {

        if (data != null) {
          state.renewalCart.splice(data, 1);
          state.cartTransactions.cartTransactions.splice(data, 1);
          if (state.renewalCart.length === 0) {
            if (state.isBusinessUser) {
              appRouter.push({ name: 'BusinessProfile' });
            } else{
              appRouter.push({ name: 'Profile' });
            }
          } 
          if(state.renewalCart.length === 0) {
            commit('setState', { property: 'step', value: 1 });
          } 
        } else {
          state.cartTransactions.cartTransactions.shift();
        }
      }
    } catch (error) {
      result = error.response;
    }
    return result;
  },

  async getRegistrationRenewalFee({ commit, state }) {
    const endpoint = state.duplicateReg ? `${SERVICE_URL}duplicateregistration/getduplicateregistrationfee` : `${SERVICE_URL}renewregistration/getregistrationrenewalfee`;
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const headers = {
      'accept': 'application/json',
      'Content-Type': 'application/json'
    }
    let cartTxn = state.cartTransactions.cartTransactions[0];
    const token = encodeURIComponent(state.accessToken);

    axios.post(`${endpoint}?accessToken=${token}&vehicleRegistrationId=${state.selectRenewRegistartion.vehicleRegistrationId}&transactionId=${cartTxn.transactionData.transactionId}`, headers, config)
    .then(result => {
      commit('setState', { property: 'extensionData', value: result.data.extensionData });
      commit('setState', { property: 'onlineTransactionFeeCollection', value: result.data.onlineTransactionFeeCollection});
      commit('setState', {property: 'step', value: 3});
    })
    .catch(error => {
      console.error(error);
    });
  },

  async getUpdateTransactionCart({ commit }) {
    commit('setState', {property: 'step', value: 4});
  },

  async getRenewPreviousStep({commit, state}) {
    let stepVal = state.step - 1;
    commit('setState', {property: 'step', value: stepVal});
  },

  async getServiceFees({ commit }, totalAmount) {
    const endpoint = `${SERVICE_URL}payment/getservicefees`;

    axios.get(`${endpoint}?totalFeeAmount=${totalAmount}`)
    .then(result => {
      commit('setState', { property: 'totalBill', value: result.data });
      commit('setState', {property: 'step', value: 5});
    })
    .catch(error => {
      console.error(error);
    });
  },

  async getPaymentProcess(context, data) {
    const endpoint = `${SERVICE_URL}payment/process`;
    const config = await getReCaptchaHeaderTBR('post', endpoint);

    let response = undefined;
    try {
      response = await axios.post(endpoint, data, config);
    } catch (error) {
      response = error.response;
    }
    return response;
  },

  async getFinalizecart({ state, rootState, commit }, data) {
    const endpoint = `${SERVICE_URL}transactionservice/finalizecart`;
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);

    console.log('FINALIZE CART CONTEXT IS', rootState);
    let response;
    try {
      const result = await axios.post(`${endpoint}?accessToken=${token}`, data, config);
      if (result.status === 200) {
        commit('setState', { property: 'finalizationCart', value: result.data });
      }
      response = result; 
    } catch (error) {
      response = error.response;
      console.error('Finalize Cart Error:', error);
    }
    return response;
  },

  async getSendTransactionConfiramtion({state, commit}, data) {
    const endpoint = `${SERVICE_URL}transactionservice/sendtransactionconfirmation`;
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);
    
    let response = undefined;
    try {
      response = await axios.post(`${endpoint}?accessToken=${token}`, data, config);
      if (response.status === 200) {
        commit('setState', {property: 'step', value: 6});
        commit('setState', {property: 'resNotificationSent', value: response.data});
      }
    } catch (error) {
      response = error.response;
    }
    return response;
  },

  async getPersonalProfileInformation({ commit, state }) {
    const endpoint = `${SERVICE_URL}customeraccount/personalprofileinformation`;
  
    const params = {
      accessToken: state.accessToken
    };

    try {
      const result = await axios.get(endpoint, { params });

      commit('setState', { property: 'personalInformation', value: result.data.onlineCustomerAccountPersonalInformation });
      commit('setState', { property: 'credentialInformation', value: result.data.onlineCustomerAccountCredentialInformation });
      commit('setState', { property: 'blocksInformation', value: result.data.onlineCustomerAccountBlocks });
      commit('setState', { property: 'reservationInformation', value: result.data.onlineCustomerPersonalReservations });
      commit('setState', { property: 'paperlessEnrollmentInformation', value: result.data.onlineCustomerAccountCustomerAttributes });

      return result; 
    } catch (error) {
      console.error(error);
    }
  },

  async getBusinessProfileInformation({ commit, state }) {
    const endpoint = `${SERVICE_URL}customeraccount/businessprofileinformation`;
  
    const params = {
      accessToken: state.accessToken
    };

    try {
      const result = await axios.get(endpoint, { params });
      commit('setState', { property: 'businessPersonalInformation', value: result.data.onlineBusinessCustomerAccountPersonalInformation });
      commit('setState', { property: 'businessBlocksInformation', value: result.data.onlineCustomerAccountBlocks });
      commit('setState', { property: 'businessReservationInformation', value: result.data.onlineCustomerPersonalReservations });
      commit('setState', { property: 'paperlessEnrollmentInformation', value: result.data.onlineCustomerAccountCustomerAttributes });

      return result; 
    } catch (error) {
      console.error(error);
    }
  },

  async getRegistrationInformation({ commit, state }) {
    commit('setState', { property: 'isLoading', value: true });
    try {
      const endpoint = `${SERVICE_URL}customeraccount/registrationinformation`;
      const params = { accessToken: state.accessToken };

      const result = await axios.get(endpoint, { params });

      commit('setState', {
        property: 'registrationInformation',
        value: result.data.onlineRegistrationDetails
      });
    } catch (error) {
      console.error(error);
    } finally {
      commit('setState', { property: 'isLoading', value: false });
    }
  },

  async getBusinessRegistrationInformation({ commit, state }) {
    commit('setState', { property: 'isLoading', value: true });
    try {
      const endpoint = `${SERVICE_URL}customeraccount/registrationinformation`;
      const params = { accessToken: state.accessToken };

      const result = await axios.get(endpoint, { params });

      commit('setState', {
        property: 'businessRegistrationInformation',
        value: result.data.onlineRegistrationDetails
      });
    } catch (error) {
      console.error(error);
    } finally {
      commit('setState', { property: 'isLoading', value: false });
    }
  },

  async getSearchRegistrationByVehicle({ commit, state }, data) {
    commit('setState', { property: 'isLoading', value: true });
    try {
      const endpoint = `${SERVICE_URL}customeraccount/searchregistrationbyvehicle`;
      const params = { 
        accessToken: state.accessToken,
        searchType: data.searchType,
        searchValue: data.searchValue,
        vehiclePlateTypeId: data.searchType === 1 ? data.vehiclePlateTypeId : 0 
      };

      const result = await axios.get(endpoint, { params });

      commit('setState', {
        property: 'businessRegistrationSearchResults',
        value: result.data.onlineRegistrationDetails || []
      });
    } catch (error) {
      console.error(error);
    } finally {
      commit('setState', { property: 'isLoading', value: false });
    }
  },

  async getAdjudicationInformation({ commit, state }) {
    const endpoint = `${SERVICE_URL}customeraccount/adjudicationinformation`;
  
    const params = {
      accessToken: state.accessToken
    };
    axios.get(endpoint, {params})
    .then(result => { 
      commit('setState', { property: 'adjudicationLicenseInformation', value: result.data.baseLicensePrivilege });
      commit('setState', { property: 'adjudicationCommercialLicenseInformation', value: result.data.commercialLicensePrivilege });
      commit('setState', { property: 'adjudicationRegistrationInformation', value: result.data.registrationPrivilege });
    })
    .catch(error => {
      console.error(error);
    });
  },

  async getBusinessAdjudicationInformation({ commit, state }) {
    const endpoint = `${SERVICE_URL}customeraccount/adjudicationinformation`;
    const params = {
      accessToken: state.accessToken
    };
    axios.get(endpoint, {params})
    .then(result => { 
      commit('setState', { property: 'businessAdjudicationRegistrationInformation', value: result.data.registrationPrivilege });
    })
    .catch(error => {
      console.error(error);
    });
  },  

  async getRenewRegistrationEligibility({ commit, state }) {
    const endpoint = `${SERVICE_URL}renewregistration/transactioneligibility`;

    const params = {
      vehicleRegistrationId: state.selectRenewRegistartion.vehicleRegistrationId,
      accessToken: state.accessToken
    };
    commit('setState', { property: 'activeAlert', value: false});
    try {
      const result = await axios.get(endpoint, { params });

      commit('setState', { property: 'renewRegistration', value: result.data });
      commit('setState', { property: 'duplicateReg', value: false });
      commit('setState', { property: 'cancelReg', value: false });

      appRouter.push({ path: '/renew-reg' });
    } catch (error) {
      commit('setState', { property: 'activeAlert', value: true});
      console.error(error);
    }
  },

  async getDuplicateRegistrationEligibility({ commit, state }) {
    const endpoint = `${SERVICE_URL}duplicateregistration/transactioneligibility`;

    const params = {
      vehicleRegistrationId: state.selectRenewRegistartion.vehicleRegistrationId,
      accessToken: state.accessToken
    };

    commit('setState', { property: 'activeAlert', value: false});
    axios.get(endpoint, {params})
    .then(result => { 
      commit('setState', { property: 'duplicateRegistration', value: result.data });
      appRouter.push({path: '/duplicate-reg'});
      commit('setState', { property: 'duplicateReg', value: true });
      commit('setState', { property: 'cancelReg', value: false });
    })
    .catch(error => {
      commit('setState', { property: 'activeAlert', value: true});
      console.error(error);
    });
  },

  async getCancelRegistrationEligibility({ commit, state }) {
    const endpoint = `${SERVICE_URL}cancelregistration/transactioneligibility`;

    const params = {
      vehicleRegistrationId: state.selectRenewRegistartion.vehicleRegistrationId,
      accessToken: state.accessToken
    };

    commit('setState', { property: 'activeAlert', value: false});
    axios.get(endpoint, {params})
    .then(result => { 
      commit('setState', { property: 'cancelRegistration', value: result.data });
      appRouter.push({path: '/cancel-reg'});
      commit('setState', { property: 'duplicateReg', value: false });
      commit('setState', { property: 'cancelReg', value: true });
    })
    .catch(error => {
      commit('setState', { property: 'activeAlert', value: true});
      console.error(error);
    });
  }, 

  async getSelectRenewReg({ commit}, renewRegValue) {  
    return commit('setState', { property: 'selectRenewRegistartion', value: renewRegValue });
  },

  async getExtendGuestLogin({ state, commit, rootState }) {
    const endpoint = `${SERVICE_URL}customeraccountlogin/extendguestloginsession`;
    const params = {
      accessToken: state.accessToken
    };

    axios.get(endpoint, {params})
    .then(result => {
      // move on to signin screen IFF it's a 200
      if (result.status === rootState.STATUSES.SUCCESS) {
      commit('setState', { property: 'accessToken', value: result.data.accessToken });
      commit('setState', { property: 'duration', value: result.data.duration });
      }
      else
      {
         // move on to signin screen is extend session fails, extend session  may fail becuase of network errors
      
      appRouter.push({ path : '/guest-signin' });
      commit('setState', { property: 'guestLogin/statusInfo', value: null });
      commit('setState', { property: 'accessToken', value: null });
      commit('setState', { property: 'personalInformation', value: null });
      commit('setState', { property: 'credentialInformation', value: null });
      commit('setState', { property: 'registrationInformation', value: null });
      commit('setState', { property: 'adjudicationLicenseInformation', value: null });
      commit('setState', { property: 'adjudicationCommercialLicenseInformation', value: null });
      commit('setState', { property: 'adjudicationRegistrationInformation', value: null });
      commit('setState', { property: 'businessPersonalInformation', value: null });
      commit('setState', { property: 'businessRegistrationInformation', value: null });
      commit('setState', { property: 'businessAdjudicationRegistrationInformation', value: null });
      sessionStorage.removeItem('ridmv');
      window.location.href = '/guest-signin' ;
      }
    })
    .catch(error => {
      console.error(error);
      appRouter.push({ path : '/guest-signin' });
      commit('setState', { property: 'guestLogin/statusInfo', value: null });
      commit('setState', { property: 'accessToken', value: null });
      commit('setState', { property: 'personalInformation', value: null });
      commit('setState', { property: 'credentialInformation', value: null });
      commit('setState', { property: 'registrationInformation', value: null });
      commit('setState', { property: 'adjudicationLicenseInformation', value: null });
      commit('setState', { property: 'adjudicationCommercialLicenseInformation', value: null });
      commit('setState', { property: 'adjudicationRegistrationInformation', value: null });
      commit('setState', { property: 'businessPersonalInformation', value: null });
      commit('setState', { property: 'businessRegistrationInformation', value: null });
      commit('setState', { property: 'businessAdjudicationRegistrationInformation', value: null });
      sessionStorage.removeItem('ridmv');
      window.location.href = '/guest-signin' ;
    });
    
  },

  async getGuestLogout({ state, commit , rootState}) {
    const endpoint = `${SERVICE_URL}customeraccountlogin/guestlogout`;
    const params = {
      accessToken: state.accessToken
    };

    axios.get(endpoint, {params})
    .then(result => {
      
      // move on to signin screen IFF it's a 200
      if (result.status === rootState.STATUSES.SUCCESS) {
      
      appRouter.push({ path : '/guest-signin' });
      commit('setState', { property: 'guestLogin/statusInfo', value: null });
      commit('setState', { property: 'accessToken', value: null });
      commit('setState', { property: 'personalInformation', value: null });
      commit('setState', { property: 'credentialInformation', value: null });
      commit('setState', { property: 'registrationInformation', value: null });
      commit('setState', { property: 'adjudicationLicenseInformation', value: null });
      commit('setState', { property: 'adjudicationCommercialLicenseInformation', value: null });
      commit('setState', { property: 'adjudicationRegistrationInformation', value: null });
      commit('setState', { property: 'businessPersonalInformation', value: null });
      commit('setState', { property: 'businessRegistrationInformation', value: null });
      commit('setState', { property: 'businessAdjudicationRegistrationInformation', value: null });
      sessionStorage.removeItem('ridmv');
      window.location.href = '/guest-signin' ;
      }
    })
    .catch(error => {
      console.error(error);
      appRouter.push({ path : '/guest-signin' });
      commit('setState', { property: 'guestLogin/statusInfo', value: null });
      commit('setState', { property: 'accessToken', value: null });
      commit('setState', { property: 'personalInformation', value: null });
      commit('setState', { property: 'credentialInformation', value: null });
      commit('setState', { property: 'registrationInformation', value: null });
      commit('setState', { property: 'adjudicationLicenseInformation', value: null });
      commit('setState', { property: 'adjudicationCommercialLicenseInformation', value: null });
      commit('setState', { property: 'adjudicationRegistrationInformation', value: null });
      commit('setState', { property: 'businessPersonalInformation', value: null });
      commit('setState', { property: 'businessRegistrationInformation', value: null });
      commit('setState', { property: 'businessAdjudicationRegistrationInformation', value: null });
      sessionStorage.removeItem('ridmv');
      window.location.href = '/guest-signin' ;
    });
  },
  
  async getValidatecart({ state, rootState, commit }, data) {
    const endpoint = `${SERVICE_URL}transactionservice/validatecart`;
    const payload = data;
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);
    
    console.log('CONTEXT IS', rootState);
    let response = undefined; 
    try {
      const result = await axios.post(`${endpoint}?accessToken=${token}`, payload, config);
      if (result.status === 200) {
        commit('setState', { property: 'validatedcart', value: result.data });
      }
      response = result; 
    } catch (error) {
      response = error.response;
    }
    return response;
  },

  async getOutputDocumentInformation({ commit, state }) {
    const endpoint = `${SERVICE_URL}duplicateregistration/getoutputdocument`;
    const params = {
      vehicleRegistrationId: state.selectRenewRegistartion.vehicleRegistrationId,
      accessToken: state.accessToken
    };

    axios.get(endpoint, {params})
    .then(result => { 
      commit('setState', { property: 'outputDocumentInformation', value: result.data });
      commit('setState', { property: 'modalDocumentType', value: true });
    })
    .catch(error => {
      console.error("getoutputdocument Error");
      console.error(error);
    });
  },

  /**
  * Gets Crash Location for UI drop down.
  * @returns {({cityId: string, description: string}|...)[]}
  */
  async getCrashLocation({ commit }) { 
    const endpoint = `${SERVICE_URL}customeraccountinboundreferencedata/getcrashlocation`
    try {
      const response = await axios.get(endpoint);
      commit('setState', { property: 'allCrashLocation', value: response.data });
    } catch (error) {
      console.error(error);
      // Handle the error gracefully
    }
  },

  /**
  * Gets US States for UI drop down.
  * @returns {({stateId: string, description: string}|...)[]}
  */
  getUsStates({ commit }) {
    const endpoint = `${SERVICE_URL}customeraccountinboundreferencedata/getusstates`

    axios.get(endpoint)
    .then(response => {
      commit('setState', { property: 'allUsStates', value: response.data });
    })
    .catch(error => {
      console.error(error);
    })
  },

  /**
  * GetsZipCodes for UI drop down.
  * @returns {({zipId: string, description: string}|...)[]}
  */
  getInstateZipCodes({ commit }) {
    const endpoint = `${SERVICE_URL}customeraccountinboundreferencedata/getrizipcode`

    axios.get(endpoint)
    .then(response => {
      commit('setState', { property: 'allInstateZipcodes', value: response.data });
    })
    .catch(error => {
      console.error(error);
    })
  },
  
  getUpdateContact({commit}, data) {
    commit('setState', { property: 'updateContact', value: data });
    commit('setState', { property: 'contactUpdateStep', value: 2 });
  },

  async getUpdateAddress({ rootState, state, commit}, data) {
    const endpoint = `${SERVICE_URL}customeraccount/validateaddress`;
    const payload = data;
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);

    return await axios.post(`${endpoint}?accessToken=${token}`, payload, config)
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) { 
        if(data.addressCategoryId == 1) {
          commit('setState', { property: 'userPhysicalAddress', value: payload });
          commit('setState', { property: 'standardPhysicalAddress', value: result.data });
        }
        else {
          commit('setState', { property: 'userMailingAddress', value: payload });
          commit('setState', { property: 'standardMailingAddress', value: result.data });
        }
      }
    })
    .catch(error => {
      console.error(error);
      const errMessage = error.response?.data?.message || 'Unknown error';
      throw new Error(errMessage);
    });
  },

  async updateBothAddresses({ commit, dispatch }, { physical, mailing }) {
    commit('setState', { property: 'isLoading', value: true });
    try {
      await Promise.all([
        dispatch('getUpdateAddress', physical),
        dispatch('getUpdateAddress', mailing)
      ]);

      // Run validation AFTER both API calls finish
      await dispatch('getCheckAddressValidate');

    } catch (error) {
      console.error(error.message);
      return; // Stop further execution
    } finally {
      commit('setState', { property: 'isLoading', value: false });
    }
  },

  async getCheckAddressValidate({ state, commit }) {
    commit('setState', { property: 'physicalAddressMsg', value: '' });
    commit('setState', { property: 'mailingAddressMsg', value: '' });

    const physicalAddress = state.standardPhysicalAddress;
    const mailingAddress = state.standardMailingAddress;

    if (!physicalAddress || !mailingAddress) return;

    const physicalAddressData = physicalAddress.individualAddressMelissaDataCollection[0];
    const mailingAddressData = mailingAddress.individualAddressMelissaDataCollection[0];

    const parseCodes = codes => codes.split(',').map(code => code.trim());

    const hasAEError = codes => codes.some(code => code.startsWith('AE'));

    const physicalCodes = parseCodes(physicalAddressData.resultcodes);
    const mailingCodes = parseCodes(mailingAddressData.resultcodes);

    const physicalError = hasAEError(physicalCodes);
    const mailingError = hasAEError(mailingCodes);

    if (physicalError) {
      commit('setState', { property: 'physicalAddressMsg', value: physicalAddressData.message });
    }

    if (mailingError) {
      commit('setState', { property: 'mailingAddressMsg', value: mailingAddressData.message });
    }

    if (!physicalError && !mailingError) {
      commit('setState', { property: 'addressUpdateStep', value: 2 });
    }

    window.scroll(0,0);
    document.body.setAttribute("tabindex", "1");
    document.body.focus();
    document.body.removeAttribute("tabindex");
    document.getElementById("app").scrollTop = 0;
  },

  async postConfirmAddress({ rootState, state}, data) {
    const endpoint = `${SERVICE_URL}customeraccount/updatecustomeraddress`;
    const payload = data;
    const config = await getReCaptchaHeaderTBR('post', endpoint);
    const token = encodeURIComponent(state.accessToken);

    const isBusiness = state.loginMode === "business";

    axios.post(`${endpoint}?accessToken=${token}`, payload, config)
    .then(result => {
      if (result.status === rootState.STATUSES.SUCCESS) { 
        if(isBusiness) {
          appRouter.push({ name: 'BusinessProfile' });
        }
        else {
          appRouter.push({ name: 'Profile' });
        }
      }
    })
    .catch(error => {
      console.error(error);
    });
  },

  async getIsEligibleForReservation({state}) {
    const endpoint = `${SERVICE_URL}customeraccount/iseligibletocreatereservation`;
    const params = {
      accessToken: state.accessToken
    };
    return await axios.get(endpoint, {params})
    .then(function (result) {
      return result.data;
    })
    .catch(error => {
      console.error(error);
    });
  },

  /**
   * Updates customer attributes (such as paperless enrollment) for the current user.
   * @param {Object} context - Vuex action context, including rootState and state.
   * @param {Array} attributes - Array of attribute objects to update, each with customerAttributeTypeId and customerAttributeStatus.
   * @returns {Promise<Object|undefined>} The Axios response object if successful, or undefined if an error occurs.
   */
  async postUpdateCustomerAttribute({ rootState, state }, attributes) {
    const endpoint = `${SERVICE_URL}customeraccount/updatecustomerattribute`;

    // Normalize attribute status to boolean for backend compatibility
    const normalizedAttributes = attributes.map(attr => ({
      customerAttributeTypeId: attr.customerAttributeTypeId,
      customerAttributeStatus: Boolean(attr.customerAttributeStatus)
    }));

    const payload = {
      customerAttribute: normalizedAttributes
    };

    const config = await getReCaptchaHeaderTBR("post", endpoint);
    const token = encodeURIComponent(state.accessToken);

    try {
      const result = await axios.post(`${endpoint}?accessToken=${token}`, payload, config);

      if (result.status === rootState.STATUSES.SUCCESS) {
        appRouter.push({ path : '/profile' });
      }

      return result;
    } catch (error) {
      console.error("Error updating customer attributes:", error);
    }
  },

  /**
 * Updates non-MFA contact fields (contactName, officePhone, officeFax) for business users
 * @param {Object} context - Vuex action context
 * @param {Object} contactData - Contact information to update
 * @returns {Promise<Object|undefined>} The Axios response object if successful
 */
  async postUpdateBusinessContactNonMfa({ rootState, state }, contactData) {
    const endpoint = `${SERVICE_URL}customeraccount/updatecustomercontactinformation`;
    
    const payload = {
      contactPersonName: contactData.contactPersonName || '',
      officePhone: contactData.officePhone || '',
      officeFax: contactData.officeFax || ''
    };

    const config = await getReCaptchaHeaderTBR("post", endpoint);
    const token = encodeURIComponent(state.accessToken);

    try {
      const result = await axios.post(`${endpoint}?accessToken=${token}`, payload, config);

      if (result.status === rootState.STATUSES.SUCCESS) {
        return result;
      }
    } catch (error) {
      console.error("Error updating business contact:", error);
    }
  },

  clearBusinessRegistrationSearchResults({ commit }) {
    commit('setState', { property: 'businessRegistrationSearchResults', value: null });
  },
};

const getters = {
  lookupInfo: state => ({
    allCrashLocation: state.allCrashLocation,
  }),
  addressLookupInfo: state => ({
    allUsStates: state.allUsStates,
    allInstateZipcodes: state.allInstateZipcodes
  }),
  statusInfo: state => ({
    customerName: state.customerName,
    dateOfBirth: state.dateOfBirth,
    lastFourDigitsOfSsn: state.lastFourDigitsOfSsn,
    licenseNumber: state.licenseNumber,
    ddNumber: state.ddNumber,

    customerId: state.customerId,
    duration: state.duration,
    sessionStartDate: state.sessionStartDate,
    sessionEndDate: state.sessionEndDate,
    sessionUid: state.sessionUid,
    accessToken: state.accessToken,
    vehicleRegistrationId: state.vehicleRegistrationId,
    renewRegistration: state.renewRegistration,

    plateType: state.plateType,

  }),
  personalInfo: state => ({
    personalInformation: state.personalInformation,
    credentialInformation: state.credentialInformation,
    blocksInformation: state.blocksInformation,
    reservationInformation: state.reservationInformation,
    paperlessEnrollmentInformation: state.paperlessEnrollmentInformation
  }),
  businessInfo: state => ({
    businessPersonalInformation: state.businessPersonalInformation,
    businessBlocksInformation: state.businessBlocksInformation,
    businessReservationInformation: state.businessReservationInformation,
    paperlessEnrollmentInformation: state.paperlessEnrollmentInformation
  }),
  registrationInfo: state => ({
    registrationInformation: state.registrationInformation,
  }),
  businessRegistrationInfo: state => ({
    businessRegistrationInformation: state.businessRegistrationInformation,
  }),
  businessRegistrationSearchInfo: state => ({
    businessRegistrationSearchResults: state.businessRegistrationSearchResults
  }),
  outputDocumentInfo: state => ({
    outputDocumentInformation: state.outputDocumentInformation
  }),
  adjudicationLicenseInfo: state => ({
    adjudicationLicenseInformation: state.adjudicationLicenseInformation,
  }),
  adjudicationCommercialLicenseInfo: state => ({
    adjudicationCommercialLicenseInformation: state.adjudicationCommercialLicenseInformation,
  }),
  adjudicationRegistrationInfo: state => ({
    adjudicationRegistrationInformation: state.adjudicationRegistrationInformation,
  }),
  businessAdjudicationRegistrationInfo: state => ({
    businessAdjudicationRegistrationInformation: state.businessAdjudicationRegistrationInformation,
  }),
  transactionInfo: state => ({
    cartTransactions: state.cartTransactions,
    transactionReasonId: state.transactionReasonId,
    transactionTypeId: state.transactionTypeId,
    transactionCartId: state.transactionCartId
  })
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
}

